import { useEffect, useState } from "react";
import {
  Container,
  HeaderContainer,
  HeaderTitleContainer,
  StatusIcon,
  IndexLabel,
  TitleLabel,
  HeaderActionContainer,
} from "./Section.styles";
import SectionContent from "./SectionContent";
import {
  TaskInProgressBadge,
  TaskToDoBadge,
  TaskCompletedBadge,
} from "./StatusBadges";
import {
  ProgressCircleTodoIcon,
  ProgressCircleInProgressIcon,
  ProgressCircleCompletedIcon,
  ChevronProgressSectionUp,
  ChevronProgressSectionDown,
} from "src/assets/icons";
import type { Task } from "./SectionContent";
import { APPLICATION_STATUSES } from "src/constants";

const taskCompletionRatioText = (tasks: Task[], isAppDrivenStatus: boolean) => {
  if (isAppDrivenStatus) return "In Progress";
  else if (!tasks || tasks.length === 0) return "No tasks";

  const total = tasks.length;
  const completed = tasks.reduce(
    (acc: number, curr: Task) => acc + (curr.status === "done" ? 1 : 0),
    0,
  );
  return `${completed}/${total} Completed`;
};

interface SectionProps {
  index: number;
  title:
    | "Invited"
    | "Submitted"
    | "Funded"
    | "Buyer Conditions"
    | "Seller Conditions"
    | "PorchPass Conditions"
    | "Declined";
  status: "In-Progress" | "Completed" | "To-Do";
  tasks: Task[];
  isNextSection?: boolean;
}

export default ({
  index,
  title,
  status,
  tasks,
  isNextSection = false,
}: SectionProps) => {
  const [isOpened, setIsOpened] = useState(isNextSection);
  const [showTasks, setShowTasks] = useState(false);
  const appDrivenStatuses = [
    APPLICATION_STATUSES.INVITED,
    APPLICATION_STATUSES.SUBMITTED,
    APPLICATION_STATUSES.DECLINED,
    APPLICATION_STATUSES.FUNDED,
  ];
  const isAppDrivenStatus = (appDrivenStatuses as readonly string[]).includes(
    title,
  );
  const openSection = () => {
    if (tasks.length === 0) return;
    else setIsOpened(!isOpened);
  };

  let currentStatusIcon = <ProgressCircleTodoIcon />;
  if (status === "In-Progress")
    currentStatusIcon = <ProgressCircleInProgressIcon />;
  if (status === "Completed")
    currentStatusIcon = <ProgressCircleCompletedIcon />;

  useEffect(() => {
    const shouldShow = !isAppDrivenStatus || status !== "Completed";
    setShowTasks(shouldShow && tasks.length > 0);
  }, [status, title, isAppDrivenStatus, tasks.length]);

  return (
    <Container isOpened={isOpened}>
      <HeaderContainer
        showTasks={showTasks}
        isAppDrivenStatus={isAppDrivenStatus}
        onClick={openSection}
      >
        <HeaderTitleContainer>
          <StatusIcon>{currentStatusIcon}</StatusIcon>
          <IndexLabel>{index}.</IndexLabel>
          <TitleLabel isOpened={isOpened} status={status}>
            {title}
          </TitleLabel>
        </HeaderTitleContainer>

        <HeaderActionContainer
          showTasks={showTasks}
          isAppDrivenStatus={isAppDrivenStatus}
        >
          {status === "To-Do" && <TaskToDoBadge>To Do</TaskToDoBadge>}
          {status === "In-Progress" && (
            <TaskInProgressBadge>
              {taskCompletionRatioText(tasks, isAppDrivenStatus)}
            </TaskInProgressBadge>
          )}
          {status === "Completed" && (
            <TaskCompletedBadge>Completed</TaskCompletedBadge>
          )}
          {!isAppDrivenStatus &&
            showTasks &&
            (isOpened ? (
              <ChevronProgressSectionUp />
            ) : (
              <ChevronProgressSectionDown />
            ))}
        </HeaderActionContainer>
      </HeaderContainer>
      {!isAppDrivenStatus && showTasks && isOpened && (
        <SectionContent tasks={tasks} />
      )}
    </Container>
  );
};
